import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Snackbar
} from '@mui/material';
import {
  Upload as UploadIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import formularioService from '../../services/formularioService';

/**
 * Componente para gestionar plantillas PDF y mapeo de campos
 * Este componente permite:
 * 1. Subir un PDF rellenable como plantilla
 * 2. Ver los campos disponibles en el PDF
 * 3. Mapear los campos del formulario con los campos del PDF
 */
const FormularioPdfManager = () => {
  // Obtener ID del formulario de la URL
  const { id: formularioId } = useParams();
  const navigate = useNavigate();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formulario, setFormulario] = useState(null);
  const [camposPDF, setCamposPDF] = useState([]);
  const [camposFormulario, setCamposFormulario] = useState([]);
  const [mapeo, setMapeo] = useState({});
  const [pdfFile, setPdfFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Función para procesar estructuras de formulario de forma segura
  const procesarEstructura = (estructuraOriginal) => {
    let estructuraProcesada = { campos: [], secciones: [] };
    
    if (!estructuraOriginal) return estructuraProcesada;
    
    if (typeof estructuraOriginal === 'string') {
      try {
        estructuraProcesada = JSON.parse(estructuraOriginal);
      } catch (e) {
        console.error('Error al parsear estructura:', e);
        return estructuraProcesada;
      }
    } else {
      estructuraProcesada = estructuraOriginal;
    }
    
    // Asegurar que existan las propiedades necesarias
    if (!Array.isArray(estructuraProcesada.campos)) {
      estructuraProcesada.campos = [];
    }
    
    if (!Array.isArray(estructuraProcesada.secciones)) {
      estructuraProcesada.secciones = [];
    }
    
    return estructuraProcesada;
  };

const construirUrlPdf = (relativePath) => {
  if (!relativePath) return null;
  
  if (relativePath.startsWith('http')) {
    return relativePath;
  }
  
  // Usar variable de entorno si está disponible
  const apiUrl = process.env.REACT_APP_API_URL;
  
  let baseUrl;
  
  if (apiUrl) {
    // Usar la URL de la variable de entorno
    baseUrl = apiUrl;
  } else {
    // Fallback: detectar entorno automáticamente
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocal) {
      baseUrl = 'http://localhost:4000';
    } else {
      baseUrl = `${window.location.origin}/sistema`;
    }
  }
  
  const cleanPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  return `${baseUrl}${cleanPath}`;//api 
};

  // Cargar formulario y datos asociados
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Cargar formulario
        const formData = await formularioService.getFormulario(formularioId);
        setFormulario(formData);
        
        // Procesar estructura del formulario
        const estructura = procesarEstructura(formData.estructura);
        
        // Extraer campos del formulario
        const camposExtraidos = [];
        
        if (estructura.campos && Array.isArray(estructura.campos)) {
          // Si hay secciones, ordenar campos por sección
          if (estructura.secciones && estructura.secciones.length > 0) {
            estructura.secciones.forEach(seccion => {
              const camposSeccion = estructura.campos
                .filter(campo => campo.seccion_id === seccion.id)
                .map(campo => ({
                  ...campo,
                  seccion: seccion.titulo
                }));
              
              camposExtraidos.push(...camposSeccion);
            });
            
            // Agregar campos sin sección
            const camposSinSeccion = estructura.campos.filter(campo => !campo.seccion_id);
            camposExtraidos.push(...camposSinSeccion);
          } else {
            // Si no hay secciones, usar todos los campos
            camposExtraidos.push(...estructura.campos);
          }
        }
        
        setCamposFormulario(camposExtraidos);
        
        // Cargar mapeo existente
        if (formData.mapeo_campos) {
          let mapeoExistente = formData.mapeo_campos;
          if (typeof mapeoExistente === 'string') {
            try {
              mapeoExistente = JSON.parse(mapeoExistente);
            } catch (e) {
              console.error('Error al parsear mapeo:', e);
              mapeoExistente = {};
            }
          }
          setMapeo(mapeoExistente || {});
        }
        
        // Cargar vista previa de PDF si existe
        if (formData.plantilla_pdf) {
          setPreviewUrl(construirUrlPdf(formData.plantilla_pdf));
          //setPreviewUrl(formData.plantilla_pdf);
          
          // Cargar campos del PDF
          try {
            const camposResponse = await formularioService.getPlantillaFields(formularioId);
            setCamposPDF(Array.isArray(camposResponse) ? camposResponse : []);
          } catch (err) {
            console.error("Error al cargar campos del PDF:", err);
            setError("No se pudieron cargar los campos del PDF");
          }
        }
      } catch (err) {
        console.error("Error al cargar formulario:", err);
        setError("No se pudo cargar la información del formulario");
      } finally {
        setLoading(false);
      }
    };
    
    if (formularioId) {
      cargarDatos();
    }
  }, [formularioId]);
  
  // Manejar selección de archivo PDF
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validar que sea un PDF
    if (file.type !== 'application/pdf') {
      setError("El archivo debe ser un PDF");
      return;
    }
    
    setPdfFile(file);
    
    // Crear URL para preview
    const fileUrl = URL.createObjectURL(file);
    setPreviewUrl(fileUrl);
  };
  
  // Subir archivo PDF
  const handleUploadPdf = async () => {
    if (!pdfFile) {
      setError("Debe seleccionar un archivo PDF");
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      // Crear FormData
      const formData = new FormData();
      formData.append('plantilla_pdf', pdfFile);
      
      // Subir archivo
      const response = await formularioService.subirPlantillaPDF(formularioId, formData);
      
      // Actualizar campos del PDF
      if (response.campos) {
        setCamposPDF(Array.isArray(response.campos) ? response.campos : 
        (typeof response.campos === 'object' ? Object.values(response.campos) : []));
      }
      
      setSuccess("Plantilla PDF cargada correctamente");
      
      // Limpiar estado del archivo
      setPdfFile(null);
      
      // Actualizar preview con la URL del servidor
      if (response.plantilla_pdf) {
        setPreviewUrl(construirUrlPdf(formData.plantilla_pdf));
        //setPreviewUrl(response.plantilla_pdf);
      }
      
      // Recargar formulario para obtener datos actualizados
      const formularioActualizado = await formularioService.getFormulario(formularioId);
      setFormulario(formularioActualizado);
    } catch (err) {
      console.error("Error al subir PDF:", err);
      setError("Error al subir la plantilla PDF: " + (err.message || err));
    } finally {
      setUploading(false);
    }
  };
  
  // Manejar cambio en mapeo de campos
  const handleMapeoChange = (campoFormulario, campoPDF) => {
    setMapeo(prev => ({
      ...prev,
      [campoFormulario]: campoPDF
    }));
  };
  
  // Guardar mapeo de campos
  const handleSaveMapeo = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Guardar mapeo
      await formularioService.guardarMapeoCampos(formularioId, mapeo);
      
      setSuccess("Mapeo de campos guardado correctamente");
      
      // Recargar formulario para obtener datos actualizados
      const formData = await formularioService.getFormulario(formularioId);
      setFormulario(formData);
    } catch (err) {
      console.error("Error al guardar mapeo:", err);
      setError("Error al guardar el mapeo de campos: " + (err.message || err));
    } finally {
      setSaving(false);
    }
  };
  
  // Limpiar mensajes de error/éxito
  const handleClearMessages = () => {
    setError(null);
    setSuccess(null);
  };
  
  // Si está cargando, mostrar indicador
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Si no hay formulario, mostrar error
  if (!formulario) {
    return (
      <Box p={3}>
        <Alert severity="error">
          No se pudo cargar el formulario
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Volver
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Alertas */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleClearMessages}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleClearMessages} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleClearMessages}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleClearMessages} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      {/* Cabecera */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              Volver
            </Button>
            <Typography variant="h4" component="h1">
              Gestión de PDF: {formulario.nombre}
            </Typography>
          </Box>
        </Box>
        
        {formulario.descripcion && (
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
            {formulario.descripcion}
          </Typography>
        )}
      </Paper>
      
      <Grid container spacing={3}>
        {/* Sección de carga de PDF */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Plantilla PDF Rellenable
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <input
                accept="application/pdf"
                id="pdf-upload"
                type="file"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="pdf-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Seleccionar PDF
                </Button>
              </label>
              
              {pdfFile && (
                <Typography variant="body2" sx={{ ml: 1, mt: 1 }}>
                  Archivo seleccionado: {pdfFile.name}
                </Typography>
              )}
            </Box>
            
            {pdfFile && (
              <Button
                variant="contained"
                color="primary"
                startIcon={uploading ? <CircularProgress size={24} /> : <SaveIcon />}
                onClick={handleUploadPdf}
                disabled={uploading}
                sx={{ mb: 2 }}
              >
                {uploading ? 'Subiendo...' : 'Subir Plantilla'}
              </Button>
            )}
            
            {/* Estado de PDF */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Estado:
              </Typography>
              {formulario.plantilla_pdf ? (
                <Alert icon={<CheckCircleIcon />} severity="success">
                  Plantilla PDF cargada
                </Alert>
              ) : (
                <Alert severity="info">
                  No hay plantilla PDF. Suba una para habilitar la generación de PDFs.
                </Alert>
              )}
            </Box>
            
            {/* Vista previa del PDF */}
            {previewUrl && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Vista previa:
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    p: 1,
                    height: 300,
                    overflow: 'hidden'
                  }}
                >
                  <iframe
                    src={previewUrl}
                    width="100%"
                    height="100%"
                    title="Vista previa del PDF"
                    style={{ border: 'none' }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PdfIcon />}
                  href={previewUrl}
                  target="_blank"
                  sx={{ mt: 1 }}
                >
                  Ver PDF completo
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Sección de mapeo de campos */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Mapeo de Campos
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            {camposPDF.length === 0 ? (
              <Alert severity="info">
                Suba una plantilla PDF para ver los campos disponibles
              </Alert>
            ) : (
              <>
                {camposFormulario.length === 0 ? (
                  <Alert severity="warning">
                    Este formulario no tiene campos definidos. Debe agregar campos al formulario antes de realizar el mapeo.
                  </Alert>
                ) : (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Asocie cada campo del formulario con un campo del PDF. Esto permitirá generar PDFs con los datos ingresados.
                    </Alert>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Campo del Formulario</TableCell>
                            <TableCell>Campo del PDF</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {camposFormulario.map((campo) => (
                            <TableRow key={campo.id}>
                              <TableCell>
                                <Typography variant="body2">
                                  {campo.etiqueta}
                                  {campo.seccion && (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      display="block"
                                    >
                                      Sección: {campo.seccion}
                                    </Typography>
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={mapeo[campo.id] || ''}
                                    onChange={(e) => handleMapeoChange(campo.id, e.target.value)}
                                    displayEmpty
                                  >
                                    <MenuItem value="">
                                      <em>No mapear</em>
                                    </MenuItem>
                                    {Array.isArray(camposPDF) && camposPDF.map((campoPdf) => (
                                      <MenuItem key={campoPdf.name} value={campoPdf.name}>
                                        {campoPdf.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Box mt={3} display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={24} /> : <SaveIcon />}
                        onClick={handleSaveMapeo}
                        disabled={saving}
                      >
                        {saving ? 'Guardando...' : 'Guardar Mapeo'}
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FormularioPdfManager;